import { Participant } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { EmptyState } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { ParticipantItem } from '../components';
import { createStyles } from '../TournamentDetailScreen.styles';

interface ParticipantsTabProps {
  participants: Participant[];
  participantName: string;
  setParticipantName: (name: string) => void;
  adding: boolean;
  isSetup: boolean;
  canManageParticipants: boolean;
  isValidCount: boolean;
  generating: boolean;
  isChallengeTournament?: boolean;
  isLeagueTournament?: boolean;
  onAddParticipant: () => void;
  onAddRegisteredParticipant: () => void;
  onDeleteParticipant: (participantId: string) => void;
  onParticipantPress: (userId: string) => void;
  onStartTournament: () => void;
  onViewBracket: () => void;
  colors: {
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
    success: string;
    badgeSetup: string;
  };
}

export const ParticipantsTab = ({
  participants,
  participantName,
  setParticipantName,
  adding,
  isSetup,
  canManageParticipants,
  isValidCount,
  generating,
  isChallengeTournament,
  isLeagueTournament,
  onAddParticipant,
  onAddRegisteredParticipant,
  onDeleteParticipant,
  onParticipantPress,
  onStartTournament,
  onViewBracket,
  colors,
}: ParticipantsTabProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  return (
    <View style={styles.tabContainer}>
      {isSetup && canManageParticipants && !isChallengeTournament && !isLeagueTournament && (
        <View style={styles.addParticipantSection}>
          <View style={styles.addParticipantRow}>
            <TextInput
              style={[
                styles.input,
                { backgroundColor: colors.card, borderColor: colors.border, color: colors.text },
              ]}
              placeholder={t('tournaments.enterParticipantName')}
              placeholderTextColor={colors.textTertiary}
              value={participantName}
              onChangeText={setParticipantName}
              editable={!adding}
            />
            <TouchableOpacity
              style={[styles.addButton, { backgroundColor: adding ? colors.border : colors.primary }]}
              onPress={onAddParticipant}
              disabled={adding}
            >
              <Text style={styles.addButtonText}>+</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={[styles.addRegisteredBtn, { borderColor: colors.primary }]}
            onPress={onAddRegisteredParticipant}
          >
            <Text style={[styles.addRegisteredBtnText, { color: colors.primary }]}>👤 {t('tournaments.addRegisteredUser')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {!isSetup && !isValidCount && participants.length > 0 && (
        <View style={[styles.warningBox, { backgroundColor: colors.badgeSetup, borderColor: '#FFC107' }]}>
          <Text style={[styles.warningText, { color: colors.text }]}>
            {t('tournaments.invalidParticipantCountWarning', { count: participants.length })}
          </Text>
        </View>
      )}

      <FlatList
        data={participants}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <ParticipantItem
            participant={item}
            canManageParticipants={canManageParticipants}
            isSetup={isSetup}
            isChallengeTournament={isChallengeTournament || isLeagueTournament}
            onPress={onParticipantPress}
            onDelete={onDeleteParticipant}
            colors={colors}
          />
        )}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="people-outline" size={52} color={colors.textTertiary} />}
            title={t('tournaments.noParticipantsYet')}
            subtitle={isSetup && canManageParticipants ? t('tournaments.addParticipants') : undefined}
            colors={colors}
          />
        }
        contentContainerStyle={styles.listContent}
      />

      {isSetup && canManageParticipants && (
        <View style={[styles.bottomActions, { backgroundColor: colors.card, borderTopColor: colors.border }]}>
          <TouchableOpacity
            style={[
              styles.startButton,
              {
                backgroundColor:
                  !isValidCount || participants.length < 2 || generating ? colors.border : colors.success,
              },
            ]}
            onPress={onStartTournament}
            disabled={!isValidCount || participants.length < 2 || generating}
          >
            <Text style={[styles.startButtonText, { color: colors.primaryText }]}>
              {generating ? t('tournaments.generating') : t('tournaments.startTournament')}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {!isSetup && (
        <View style={[styles.bottomActions, { backgroundColor: colors.card, borderTopColor: colors.border }]}>
          <TouchableOpacity style={[styles.viewBracketButton, { backgroundColor: colors.primary }]} onPress={onViewBracket}>
            <Text style={[styles.viewBracketButtonText, { color: colors.primaryText }]}>{t('tournaments.viewBracket')}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};
